import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appSignInConfiguration", "builtByHubSpot", "connectionType", "features", "installUrl", "isDashworks", "isOpsHub", "onClick", "onConnectionComplete", "onInstallComplete", "onInstallError", "previewListingId", "productFeatures", "slug", "sourceId", "showSecondaryCta", "fullWidth", "onEmbedClose"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import unescapedText from 'I18n/utils/unescapedText';
import PortalIdParser from 'PortalIdParser';
import { useOpenCopilot } from 'copilot-toolkit/hooks/useOpenCopilot';
import { Flex } from 'foundations-components/components/Flex';
import { gate } from 'hub-http/gates';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Link } from 'foundations-components/transitional/components/Link';
import { H2 } from 'foundations-components/transitional/components/H2';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { AppConnectionStatuses } from 'integrations-lib/constants/appConnectionStatuses';
import { ReconnectButton } from 'integrations-reconnect-lib';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import enviro from 'enviro';
import Raven from 'raven-js';
import { useSendInstallIntentLazyQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
import { useGetPortalEligibilityForSensitiveDataQuery, useGetSensitiveDataStateForPortalQuery } from '../../__generated__/chirp/com/hubspot/sensitive/data/status/controller/rpc/SensitiveDataStatusControllerFrontendService.hooks';
import { useListSupportedAppsQuery } from '../../__generated__/chirp/com/hubspot/unifiedaccount/rpc/AppRpc.hooks';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { useGetOAuthUrl } from '../hooks/useGetOAuthUrl';
import { getBreezeStudioUrl, getConnectedAppsUrl, getFormattedShortlinkUrl, getSettingsRouterUrl, navigateTo } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from 'marketplace-ui-common/utils/TrackingUtils';
import { PostInstallButton } from './PostInstallButton';
import { RequestPermissionsModal } from './RequestPermissionsModal';
import { useAppStatus } from './hooks/useAppStatus';
import { useGetInstallConfiguration } from './hooks/useGetInstallConfiguration';
import { useBreezeAssistantBetaListing } from '../functionality/useBreezeAssistantBetaListing';
import { useGetMcpServer } from './hooks/useGetMcpServer';
import { InstallWizard } from './installWizard/InstallWizard';
import { cleanupExpiredRequests, hasActiveInstallRequest } from './utils/installRequestTracking';
import { AppGovernanceRequestButton } from '../governance/AppGovernanceRequestButton';
import { useGetAppsGovernanceStatusQuery } from '../../__generated__/chirp/com/hubspot/applications/governance/rpc/ApplicationGovernanceAppAuthRpc.hooks';
import { useGetProductApprovalConfiguration } from 'marketplace-ui-common/governance/hooks/useGetProductApprovalConfiguration';
import { SensitiveDataInstallModal } from './SensitiveDataInstallModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AsyncIntegrationsConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-button' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectButton')
});
const AsyncIntegrationsConnectModal = Loadable({
  loader: () => import( /* webpackChunkName: 'integrations-connect-modal' */'ui-addon-integrations-directory-panel/UIIntegrationsConnectModal')
});
const AsyncDataSyncConnectButton = Loadable({
  loader: () => import( /* webpackChunkName: 'data-sync-connect-button' */'ui-universal-auth/DataSyncAuthButton')
});
const AsyncDashworksAuthButton = Loadable({
  loader: () => import( /* webpackChunkName: 'dashworks-auth-button' */'ui-universal-auth/DashworksAuthButton').then(module => module.DashworksAuthButton)
});
const AsyncMCPAuthButton = Loadable({
  loader: () => import( /* webpackChunkName: 'mcp-auth-button' */'ui-universal-auth/MCPAuthButton').then(module => module.MCPAuthButton)
});
const AsyncUnifiedAccountAuthButton = Loadable({
  loader: () => import( /* webpackChunkName: 'unified-account-button' */'unified-auth').then(module => module.ConnectAccount)
});
const ALERT_TIMEOUT = 3000;

// Temporary: Simplify after beta rollout — remove the isBreezeAssistantBeta branch
// and make the Breeze Assistant alert the default.
export function buildMcpConnectedAlertMessage({
  isBreezeAssistantBeta,
  trackInteraction,
  openCopilot,
  rest
}) {
  if (!isBreezeAssistantBeta) {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "400",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.mcpServerConnectedSuccessAlert.bodyText"
      }), /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, Object.assign({
          href: getBreezeStudioUrl(),
          external: true,
          size: "small",
          use: "tertiary",
          onClick: () => {
            trackInteraction({
              action: 'click breeze studio from mcp connected alert'
            });
          }
        }, rest, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.mcpServerConnectedSuccessAlert.breezeStudioButton"
          })
        }))
      })]
    });
  }
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "detailsPage.mcpServerConnectedSuccessAlert.breezeAssistantBodyText_jsx",
    elements: {
      Link
    },
    options: {
      linkProps: {
        onClick: () => {
          openCopilot();
          trackInteraction({
            action: 'click breeze assistant from mcp connected alert'
          });
        }
      }
    }
  });
}
export function AppCTAButton(_ref) {
  var _userInfo$user$roles$, _userInfo$user, _userInfo$gates, _supportedAppsData$su, _uninstallableReason$, _userInfo$user2, _eligibilityData$isEl;
  let {
      appSignInConfiguration,
      builtByHubSpot,
      connectionType,
      features,
      installUrl = '',
      isDashworks,
      isOpsHub,
      onClick,
      onConnectionComplete,
      onInstallComplete = () => {},
      onInstallError = () => {},
      previewListingId,
      productFeatures,
      slug,
      sourceId,
      showSecondaryCta = false,
      fullWidth = true,
      onEmbedClose
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const openCopilot = useOpenCopilot();
  const {
    appContext,
    iconUrl,
    isEmbedded,
    isMcpServer,
    isMcpConnector,
    listingId,
    listingIdV2,
    name,
    productType,
    published,
    userInfo,
    trackInstall,
    trackInteraction
  } = useDetailsPageContext();
  const [standardInstallModalOpen, setStandardInstallModalOpen] = useState(false);
  const [homemadeIframeModalOpen, setHomemadeIframeModalOpen] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [sensitiveDataModalOpen, setSensitiveDataModalOpen] = useState(false);
  const [sendInstallIntent] = useSendInstallIntentLazyQuery();
  const [dashworksInstalled, setDashworksInstalled] = useState(false);

  // Temporary beta hook: Remove after Breeze Assistant MCP beta rollout
  const isBreezeAssistantBeta = useBreezeAssistantBetaListing();
  const isPreview = !published || !!previewListingId;
  const isSuperAdmin = (_userInfo$user$roles$ = userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 || (_userInfo$user = _userInfo$user.roles) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.includes('super-admin')) !== null && _userInfo$user$roles$ !== void 0 ? _userInfo$user$roles$ : false;
  const isUngatedForConnectionEmbed = userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate('ConnectedApps::ConnectionsEmbed'));

  // Cleanup expired install requests on mount
  useEffect(() => {
    cleanupExpiredRequests();
  }, []);
  const isUIE = useMemo(() => {
    return !!(productFeatures !== null && productFeatures !== void 0 && productFeatures.some(feature => feature.type === 'UIE'));
  }, [productFeatures]);
  const mcpServerVisibility = useMemo(() => {
    var _productFeatures$find;
    return productFeatures === null || productFeatures === void 0 || (_productFeatures$find = productFeatures.find(feature => feature.type === 'MCP_SERVER')) === null || _productFeatures$find === void 0 || (_productFeatures$find = _productFeatures$find.data) === null || _productFeatures$find === void 0 ? void 0 : _productFeatures$find.visibility;
  }, [productFeatures]);

  // Only show the MCPAuthButton for explicitly GLOBALLY_VISIBLE servers.
  // All other cases (INSTALL_REQUIRED or no visibility field) use the standard
  // seamless install button.
  const isGloballyVisibleMcpServer = isMcpServer && mcpServerVisibility === 'GLOBALLY_VISIBLE';

  // Do not use sourceId as a number for AGENT productType. Known issue with globalId size causing parseInt to incorrectly convert.
  const sourceIdAsNumber = sourceId ? typeof sourceId === 'number' ? sourceId : parseInt(sourceId, 10) : undefined;
  const {
    data: supportedAppsData,
    loading: supportedAppsLoading
  } = useListSupportedAppsQuery({
    skip: !sourceIdAsNumber || appContext !== 'app' || !builtByHubSpot
  });
  const isUnifiedAccount = builtByHubSpot && supportedAppsData ? (_supportedAppsData$su = supportedAppsData.supportedApps) === null || _supportedAppsData$su === void 0 ? void 0 : _supportedAppsData$su.some(app => app.appId === sourceIdAsNumber) : undefined;
  const {
    data: appGovernanceStatusData
  } = useGetAppsGovernanceStatusQuery({
    variables: {
      appIds: [sourceIdAsNumber || 0]
    },
    skip: !sourceIdAsNumber || appContext !== 'app'
  });
  const isAppGovernable = useMemo(() => {
    if (!sourceIdAsNumber) {
      return false;
    }
    return (appGovernanceStatusData === null || appGovernanceStatusData === void 0 ? void 0 : appGovernanceStatusData.appGovernanceStatuses[sourceIdAsNumber]) === 'GOVERNED';
  }, [appGovernanceStatusData, sourceIdAsNumber]);
  const {
    enabled: isProductApprovalEnabled
  } = useGetProductApprovalConfiguration({
    skip: appContext !== 'app'
  });

  // Fetch MCP server access for MCP server listings
  const {
    matchingMcpServer,
    loading: mcpServersLoading
  } = useGetMcpServer({
    appContext,
    isMcpServer: isGloballyVisibleMcpServer,
    sourceId: sourceIdAsNumber
  });
  const {
    loading: installConfigurationLoading,
    installState,
    installType,
    refetchInstallability,
    installLabel,
    installLabelText,
    uninstallableReason,
    uninstallableTooltipContent,
    requestInstallLabel,
    postInstallLabel,
    postInstallRoute,
    postInstallOnClick,
    postInstallRecommendedAction
  } = useGetInstallConfiguration({
    productType,
    appContext,
    name,
    installUrl,
    features,
    isDashworks,
    hasMcpServerAccess: !!matchingMcpServer,
    isMcpConnector,
    isMcpServer: isGloballyVisibleMcpServer,
    isOpsHub,
    isUnifiedAccount,
    listingId,
    connectionType,
    sourceId,
    published,
    isUngatedForConnectionEmbed,
    onEmbedClose
  });

  // enabled if app is governable, product approval is turned on for portal, and user is only missing integrations-management scope
  const enabledForAppGovernance = appContext === 'app' && isAppGovernable && sourceIdAsNumber && isProductApprovalEnabled && (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_USER_PERMISSIONS' && ((_uninstallableReason$ = uninstallableReason.missingUserRoles) === null || _uninstallableReason$ === void 0 ? void 0 : _uninstallableReason$.length) === 1 && uninstallableReason.missingUserRoles[0] === 'integrations-management';
  const {
    pollForStatus
  } = useAppStatus({
    appId: sourceId,
    shouldFetch: appContext === 'app',
    currentUserId: (userInfo === null || userInfo === void 0 || (_userInfo$user2 = userInfo.user) === null || _userInfo$user2 === void 0 ? void 0 : _userInfo$user2.user_id) || 0
  });
  const onInstallClickDefault = useCallback(() => {
    void sendInstallIntent({
      variables: {
        slug
      }
    });
    onClick === null || onClick === void 0 || onClick();
    trackInstall({});
    trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
  }, [onClick, sendInstallIntent, slug, trackInstall]);
  const isStandardInstallType = installType === 'STANDARD';
  const {
    data: oauthUrlData,
    loading: oauthUrlLoading,
    refetch: refetchOAuthUrl
  } = useGetOAuthUrl({
    listingId: previewListingId || listingId,
    published,
    skip: appContext === 'public' || !isStandardInstallType,
    useInternalAuth: appContext === 'admin-preview'
  });
  const oauthUrl = oauthUrlData === null || oauthUrlData === void 0 ? void 0 : oauthUrlData.oauthUrl;
  useEffect(() => {
    if (appContext === 'app' && isStandardInstallType && listingId) {
      void refetchOAuthUrl();
    }
  }, [appContext, isStandardInstallType, listingId, published, refetchOAuthUrl]);

  // Sensitive data is enabled asynchronously and can take many minutes. Rather than
  // guess with a client-side timer, drive the CTA off the real portal SD state. We only
  // query/poll while the app actually requires sensitive data.
  // is an app-auth call, so we gate it to the 'app' context (like useGetOAuthUrl) — it would
  // fail in the public storefront and is irrelevant in preview/admin-preview.
  const requiresSensitiveData = !isPreview && appContext === 'app' && (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'SENSITIVE_DATA_REQUIRED';
  const {
    data: sensitiveDataStateData,
    refetch: refetchSensitiveDataState
  } = useGetSensitiveDataStateForPortalQuery({
    skip: !requiresSensitiveData,
    pollInterval: requiresSensitiveData ? 5000 : 0,
    fetchPolicy: 'cache-and-network'
  });
  const {
    data: eligibilityData,
    loading: eligibilityLoading
  } = useGetPortalEligibilityForSensitiveDataQuery({
    skip: !requiresSensitiveData
  });
  const isPortalSensitiveDataEligible = eligibilityLoading || ((_eligibilityData$isEl = eligibilityData === null || eligibilityData === void 0 ? void 0 : eligibilityData.isEligibleForSensitiveData) !== null && _eligibilityData$isEl !== void 0 ? _eligibilityData$isEl : true);
  const sensitiveDataState = sensitiveDataStateData === null || sensitiveDataStateData === void 0 ? void 0 : sensitiveDataStateData.sensitiveDataState;
  // The portal has opted in and provisioning is still in flight.
  const sensitiveDataEnabling = requiresSensitiveData && (sensitiveDataState === 'ONBOARDING_REQUESTED' || sensitiveDataState === 'PROVISIONING');

  // Once provisioning completes, refresh installability so the requirement clears.
  useEffect(() => {
    if (requiresSensitiveData && sensitiveDataState === 'ENABLED') {
      void refetchInstallability();
    }
  }, [requiresSensitiveData, sensitiveDataState, refetchInstallability]);
  const handleConnectionComplete = useCallback(payload => {
    if (onConnectionComplete) {
      // Analytics TO DO
      onConnectionComplete(payload);
    } else if (payload) {
      const redirectUrl = payload && payload.meta && payload.meta.redirectUrl;
      // In marketplace-ui-details, we might want to show a success message instead of redirecting
      if (redirectUrl) {
        navigateTo(redirectUrl, isEmbedded);
      } else {
        if (payload.appId) {
          navigateTo(getSettingsRouterUrl({
            appId: payload.appId
          }), isEmbedded);
        } else {
          navigateTo(getConnectedAppsUrl(), isEmbedded);
        }
      }
    }
    onInstallComplete();
  }, [isEmbedded, onConnectionComplete, onInstallComplete]);
  const handleConnectionFailed = useCallback(err => {
    var _err$meta, _err$meta2;
    const errorType = ((_err$meta = err.meta) === null || _err$meta === void 0 ? void 0 : _err$meta.type) || 'default';
    const errorMessage = (_err$meta2 = err.meta) === null || _err$meta2 === void 0 ? void 0 : _err$meta2.appErrorMessage;

    // For INTEGRATION_SPECIFIC_ERROR, use the errorMessage directly as the message
    if (errorType === 'INTEGRATION_SPECIFIC_ERROR' && errorMessage) {
      FloatingAlertStore.addAlert({
        message: errorMessage,
        titleText: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.titleText`, {
          name
        }),
        type: 'danger'
      });
    } else {
      // For all other error types, use the static translations
      FloatingAlertStore.addAlert({
        message: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.message`, {
          name
        }),
        titleText: unescapedText(`detailsPage.productDetailsCard.cta.error.${errorType}.titleText`, {
          name
        }),
        type: 'danger'
      });
    }
    onInstallError();
  }, [name, onInstallError]);

  // Determine which button to render
  let renderedButton;
  if (isPreview && connectionType !== 'STANDARD') {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      use: "primary"
    }, rest, {
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.productDetailsCard.cta.install.${productType}`
      })
    }));
  } else if (installState === 'RECOMMENDED_ACTION' && postInstallRecommendedAction && postInstallRecommendedAction.url) {
    renderedButton = /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: '200',
      children: [/*#__PURE__*/_jsx(ReconnectButton, {
        buttonProps: Object.assign({
          use: 'primary'
        }, rest),
        label: postInstallRecommendedAction.label,
        metadata: {
          appId: sourceIdAsNumber,
          status: AppConnectionStatuses.DISCONNECTED,
          accountId: postInstallRecommendedAction.accountId,
          unifiedAccountId: postInstallRecommendedAction.unifiedAccountId
        },
        onAuthStarted: () => {
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_RECONNECT');
          trackInteraction({
            action: 'open reconnect popup'
          });
        },
        onAuthSucceeded: () => {
          trackInteraction({
            action: 'reconnect succeeded'
          });
          postInstallRecommendedAction === null || postInstallRecommendedAction === void 0 || postInstallRecommendedAction.refetch();
        },
        onAuthCancelled: () => {
          FloatingAlertStore.addAlert({
            titleText: unescapedText('detailsPage.productDetailsCard.alerts.reconnectCancelled.title'),
            message: unescapedText('detailsPage.productDetailsCard.alerts.reconnectCancelled.message'),
            sticky: true,
            type: 'warning',
            timeout: ALERT_TIMEOUT
          });
          trackInteraction({
            action: 'reconnect cancelled'
          });
        },
        onAuthFailed: args => {
          FloatingAlertStore.addAlert({
            titleText: unescapedText('detailsPage.productDetailsCard.alerts.reconnectFailed.title'),
            message: unescapedText('detailsPage.productDetailsCard.alerts.reconnectFailed.message'),
            sticky: true,
            type: 'danger',
            timeout: ALERT_TIMEOUT
          });
          if (enviro.deployed('sentry')) {
            Raven.captureException(args.error);
          } else {
            console.error(args.error);
            console.error('Error message', args.errorMessage);
          }
        },
        url: postInstallRecommendedAction.url
      }), showSecondaryCta && /*#__PURE__*/_jsx(PostInstallButton, Object.assign({
        marginInlineStart: "0",
        use: "secondary",
        postInstallLabel: postInstallLabel,
        postInstallRoute: postInstallRoute,
        postInstallOnClick: postInstallOnClick
      }, rest))]
    });
  } else if (installState === 'INSTALLED' || dashworksInstalled) {
    renderedButton = /*#__PURE__*/_jsx(PostInstallButton, Object.assign({
      postInstallLabel: postInstallLabel,
      postInstallRoute: postInstallRoute,
      postInstallOnClick: postInstallOnClick
    }, rest));
  } else if (installConfigurationLoading || oauthUrlLoading || mcpServersLoading || supportedAppsLoading) {
    renderedButton = /*#__PURE__*/_jsx(LoadingButton, Object.assign({
      loading: true,
      role: "button",
      size: "medium",
      use: "primary"
    }, rest, {
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.APP"
      })
    }));
  }
  // Renders an "Enable sensitive data" button when the backend reports SENSITIVE_DATA_REQUIRED
  else if (!isPreview && (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'SENSITIVE_DATA_REQUIRED') {
    renderedButton = /*#__PURE__*/_jsxs(Fragment, {
      children: [!isPortalSensitiveDataEligible ? /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.enableSensitiveDataRequiresEnterprise"
        }),
        children: /*#__PURE__*/_jsx(Button, {
          use: "primary",
          disabled: true,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.enableSensitiveData"
          })
        })
      }) : !isSuperAdmin ? /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.enableSensitiveDataNotSuperAdmin"
        }),
        children: /*#__PURE__*/_jsx(Button, {
          use: "primary",
          disabled: true,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.enableSensitiveData"
          })
        })
      }) : sensitiveDataEnabling ? /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.enablingSensitiveData"
        })
      }) : /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary"
      }, rest, {
        onClick: () => {
          trackInteraction({
            action: 'click enable sensitive data button'
          });
          setSensitiveDataModalOpen(true);
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.enableSensitiveData"
        })
      })), sensitiveDataModalOpen && /*#__PURE__*/_jsx(SensitiveDataInstallModal, {
        onClose: () => setSensitiveDataModalOpen(false),
        onSuccess: () => {
          setSensitiveDataModalOpen(false);
          // Pull the fresh SD state immediately so the CTA flips to the
          // disabled "enabling" state without waiting for the next poll.
          void refetchSensitiveDataState();
        }
      })]
    });
  }
  // Renders a "Request to install" button for missing user permissions
  else if (!isPreview &&
  // if isPreview has reached this component, we want to make it to the STANDARD installType logic block
  (uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.label) === 'MISSING_USER_PERMISSIONS') {
    if (enabledForAppGovernance) {
      //use new app governance request flow if app is governable, has valid source id, and portal is enabled for product approval
      renderedButton = /*#__PURE__*/_jsx(AppGovernanceRequestButton, {
        appId: sourceIdAsNumber,
        appName: name,
        trackInteraction: action => {
          trackInteraction({
            action
          });
        }
      });
    } else {
      const hasActiveRequest = requestSent || hasActiveInstallRequest(listingId || 0);
      renderedButton = /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          disabled: !hasActiveRequest,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.requestAlreadySent"
          }),
          children: /*#__PURE__*/_jsx(Button, Object.assign({
            onClick: () => {
              onClick === null || onClick === void 0 || onClick();
              setOpenPermissionsModal(true);
              trackInteraction({
                action: 'click request install app permission button'
              });
            },
            use: "primary"
          }, rest, {
            disabled: hasActiveRequest,
            children: requestInstallLabel
          }))
        }), openPermissionsModal && /*#__PURE__*/_jsx(RequestPermissionsModal, {
          legacyListingId: listingIdV2 !== null && listingIdV2 !== void 0 ? listingIdV2 : 0,
          listingId: listingId !== null && listingId !== void 0 ? listingId : 0,
          missingUserRoles: uninstallableReason.label === 'MISSING_USER_PERMISSIONS' ? uninstallableReason.missingUserRoles : [],
          onClose: () => setOpenPermissionsModal(false),
          onSubmit: () => setRequestSent(true)
        })]
      });
    }
  }
  // Disabled or Trial button with tooltip for all other uninstallable reasons
  else if (!isPreview &&
  // if isPreview has reached this component, we want to make it to the STANDARD installType logic block
  installState === 'UNINSTALLABLE' && uninstallableReason && uninstallableTooltipContent) {
    renderedButton = /*#__PURE__*/_jsx(Tooltip, {
      title: uninstallableTooltipContent,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        onClick: () => {
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
          trackInstall({});
        },
        role: "button",
        use: "primary"
      }, rest, {
        disabled: true,
        children: installLabel
      }))
    });
  }
  // Apps exempt from seamless install flow - show only Schedule Demo button
  else if (installType === 'NONE' ||
  // LegacyInstallType check for installType === 'NONE'
  // Can remove when Marketplace:InstallType gate is cleaned up
  installType === 'STANDARD' && !installUrl) {
    renderedButton = null;
  }
  // Button that renders standard InstallWizard for apps with configured OAuth URLs
  else if (installType === 'STANDARD' && oauthUrl) {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      onClick: () => {
        onClick === null || onClick === void 0 || onClick();
        if (appContext !== 'admin-preview' || !previewListingId) {
          void sendInstallIntent({
            variables: {
              slug
            }
          });
        }
        setStandardInstallModalOpen(true);
        trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
        trackInteraction({
          action: 'open install wizard'
        });
        trackInstall({});
      },
      use: "primary"
    }, rest, {
      children: installLabel
    }));
  }
  // Simple button with href for LINK installType
  else if (installType === 'LINK' ||
  // LegacyInstallType versions of installType === 'LINK'
  // Can remove when Marketplace:InstallType gate is cleaned up
  installType === 'LEGACY' || installType === 'HOMEMADE_EXTERNAL') {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      external: true,
      href: getFormattedShortlinkUrl(installUrl),
      onClick: onInstallClickDefault,
      role: "button",
      use: "primary"
    }, rest, {
      children: installLabel
    }));
  }
  // Data sync auth button
  else if (installType === 'HOMEMADE_DATA_SYNC') {
    renderedButton = /*#__PURE__*/_jsx(AsyncDataSyncConnectButton, Object.assign({
      appId: sourceIdAsNumber !== null && sourceIdAsNumber !== void 0 ? sourceIdAsNumber : 0,
      onClick: onInstallClickDefault,
      onComplete: onInstallComplete,
      portalId: PortalIdParser.get(),
      trackingSource: "marketplace",
      use: "primary"
    }, rest, {
      children: installLabel
    }));
  }
  // Dashworks auth button
  else if (installType === 'HOMEMADE_DASHWORKS') {
    renderedButton = /*#__PURE__*/_jsx(AsyncDashworksAuthButton, Object.assign({
      appId: sourceIdAsNumber !== null && sourceIdAsNumber !== void 0 ? sourceIdAsNumber : 0,
      onCancel: () => {
        trackInteraction({
          action: 'dashworks app connection cancelled'
        });
      },
      onClick: onInstallClickDefault,
      onComplete: __secretId => {
        trackInteraction({
          action: 'dashworks app connected'
        });
        onInstallComplete();
        setDashworksInstalled(true);
        if (!isEmbedded) {
          openCopilot();
        }
      },
      portalId: PortalIdParser.get(),
      use: "primary"
    }, rest, {
      children: installLabel
    }));
  }
  // MCP Server auth button — only for explicitly GLOBALLY_VISIBLE servers
  else if (installType === 'HOMEMADE_MCP_SERVER' && matchingMcpServer && isGloballyVisibleMcpServer) {
    var _matchingMcpServer$cl, _matchingMcpServer$mc;
    renderedButton = /*#__PURE__*/_jsx(AsyncMCPAuthButton
    // GLOBALLY_VISIBLE servers use an empty scope string for now; still working out final scope values
    // slack thread: https://hubspot.slack.com/archives/C09PTCDDWLA/p1773869191269339?thread_ts=1773330756.488929&cid=C09PTCDDWLA
    , Object.assign({
      oAuthScope: "",
      authenticatorId: matchingMcpServer.authenticatorId,
      mcpServerId: matchingMcpServer.id,
      appName: name,
      mcp: {
        clientId: (_matchingMcpServer$cl = matchingMcpServer.clientId) !== null && _matchingMcpServer$cl !== void 0 ? _matchingMcpServer$cl : '',
        serverUrl: (_matchingMcpServer$mc = matchingMcpServer.mcpUrl) !== null && _matchingMcpServer$mc !== void 0 ? _matchingMcpServer$mc : ''
      },
      onCancel: () => {
        trackInteraction({
          action: 'mcp server connection cancelled'
        });
      },
      onClick: onInstallClickDefault,
      onComplete: __secretId => {
        trackInteraction({
          action: 'mcp server connected'
        });
        FloatingAlertStore.addAlert({
          sticky: true,
          titleText: unescapedText('detailsPage.mcpServerConnectedSuccessAlert.titleText', {
            name
          }),
          message: buildMcpConnectedAlertMessage({
            isBreezeAssistantBeta,
            trackInteraction,
            openCopilot,
            rest
          }),
          type: 'success'
        });
        void refetchInstallability();
        onInstallComplete();
      },
      portalId: PortalIdParser.get(),
      use: "primary"
    }, rest, {
      children: installLabel
    }));
  }
  // New unified-auth flow meant to eventually replace HOMEMADE_MCP_SERVER, HOMEMADE_DATA_SYNC, HOMEMADE_DASHWORKS
  else if (installType === 'HOMEMADE_UNIFIED_ACCOUNT') {
    renderedButton = /*#__PURE__*/_jsx(AsyncUnifiedAccountAuthButton, Object.assign({
      use: "primary",
      appId: sourceIdAsNumber !== null && sourceIdAsNumber !== void 0 ? sourceIdAsNumber : 0,
      portalId: PortalIdParser.get(),
      appName: name,
      onClick: onInstallClickDefault,
      onComplete: () => {
        void refetchInstallability();
        onInstallComplete();
      }
    }, rest, {
      children: installLabel
    }));
  }
  // Iframe modal button for builtByHubSpot modal connections
  else if (installType === 'HOMEMADE_MODAL') {
    renderedButton = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        onClick: () => {
          onClick === null || onClick === void 0 || onClick();
          void sendInstallIntent({
            variables: {
              slug
            }
          });
          setHomemadeIframeModalOpen(true);
          trackInstall({});
          trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
        },
        use: "primary"
      }, rest, {
        children: installLabel
      })), homemadeIframeModalOpen && /*#__PURE__*/_jsxs(Modal, {
        use: "default",
        width: 1000,
        children: [/*#__PURE__*/_jsxs(DialogHeader, {
          children: [/*#__PURE__*/_jsx(DialogCloseButton, {
            onClick: () => {
              setHomemadeIframeModalOpen(false);
              void refetchInstallability();
            }
          }), /*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.install.homemadeModalDialog.header",
              options: {
                appName: name
              }
            })
          })]
        }), /*#__PURE__*/_jsx(DialogBody, {
          paddingBlockEnd: "400",
          children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectModal, {
            appId: sourceId !== null && sourceId !== void 0 ? sourceId : listingId,
            onConnectionComplete: handleConnectionComplete,
            onIntegrationNotFound: () => {
              console.log('Integration not found');
            },
            portalId: PortalIdParser.get(),
            sourceAppId: "app-marketplace",
            text: installLabelText
          })
        })]
      })]
    });
  }
  // Default integrations connect button for builtByHubSpot iframe
  else if (installType === 'HOMEMADE_IFRAME') {
    renderedButton = /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectButton, Object.assign({
        use: "primary",
        "data-test-id": "cta-button",
        appId: sourceId !== null && sourceId !== void 0 ? sourceId : 0,
        fullWidth: fullWidth,
        onClick: onInstallClickDefault,
        onConnectionComplete: handleConnectionComplete,
        onConnectionFailed: handleConnectionFailed,
        portalId: PortalIdParser.get(),
        sourceAppId: "app-marketplace",
        text: installLabelText
      }, rest))
    });
  }
  // Fallback button
  else {
    renderedButton = /*#__PURE__*/_jsx(Button, Object.assign({
      use: "primary"
    }, rest, {
      disabled: true,
      children: installLabel
    }));
  }
  const oauthQueryParams = oauthUrl ? new URL(oauthUrl).search : '';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderedButton, standardInstallModalOpen && /*#__PURE__*/_jsx(InstallWizard, {
      appContext: appContext,
      appSignInConfiguration: appSignInConfiguration,
      appId: String(sourceId !== null && sourceId !== void 0 ? sourceId : ''),
      features: features,
      iconUrl: iconUrl,
      installUrl: installUrl,
      isPreview: isPreview || appContext === 'admin-preview',
      isUIE: isUIE,
      listingId: listingId,
      name: name,
      oauthQueryParams: oauthQueryParams,
      onClose: () => {
        trackInteraction({
          action: 'close install wizard'
        });
        setStandardInstallModalOpen(false);
        void refetchInstallability();
        pollForStatus();
      },
      portalId: PortalIdParser.get()
    })]
  });
}